<template>
  <div class="flex-auto px-4 lg:px-10 py-4">
    <div>
      <form>
        <div class="flex flex-wrap justify-between items-center py-4">
          <div
            v-if="blogOperationMode === 'create'"
            class="w-full lg:w-4/12 px-4"
          >
            <div class="relative w-full mb-3">
              <label
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="'Instance Name'"
                  :description="'Name of the blog instance'"
                />
              </label>
              <input
                v-model="formDataLocal['instanceName']"
                type="text"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
          </div>
          <div class="w-full lg:w-4/12 px-4">
            <div class="relative w-full mb-3">
              <label
                class="block uppercase text-blueGray-600 text-xs font-bold mb-2"
              >
                <AttrTitleWithDescription
                  :title="'Blog Title'"
                  :description="'Enter a blog title'"
                />
              </label>
              <input
                v-model="formDataLocal['blog']['abi_blog_title']"
                type="text"
                class="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
          </div>
          <div class="w-full lg:w-4/12 px-4 flex justify-end">
            <button
              @click="handleCopyShortcode"
              v-if="blogOperationMode === 'edit'"
              type="button"
              :class="
                shortCodeCopied
                  ? 'bg-green-500 active:bg-green-600'
                  : 'bg-yellow-500 active:bg-yellow-600'
              "
              class="mr-1 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
            >
              <p v-if="!shortCodeCopied">shortcode</p>
              <p v-else>copied</p>
            </button>
            <button
              type="button"
              @click="handleAddSection"
              class="bg-gray-500 text-white active:bg-gray-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
            >
              + Add
            </button>
          </div>
        </div>
      </form>
    </div>
    <!-- Blog Section {{{ -->
    <div
      v-for="(component, i) in formDataLocal['components']"
      :key="i"
      class="rounded-lg p-4 mx-2 my-4 shadow bg-white"
    >
      <div class="p-4 rounded-md bg-gray-100 flex items-center justify-between">
        <p class="bold text-lg ml-4 mb-2 text-gray-500">
          <b>{{
            component["component"]
              ? `SECTION ${i + 1}: ` +
                componentNameFormat(component["component"])
              : "SELECT A COMPONENT"
          }}</b>
        </p>
        <!-- select input to select which component to insert -->
        <div class="flex items-center">
          <select
            v-model="component['component']"
            class="border-0 px-3 py-3 w-[350px] placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
          >
            <option
              v-for="(option, index) in changeOptionsFormat(
                basicBlogComponents
              )"
              :key="index"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <button
            :disabled="this.formData.components.length === 1"
            @click="handleDelete(i)"
            :class="
              this.formData.components.length === 1
                ? 'bg-gray-200 cursor-not-allowed'
                : 'bg-gray-300'
            "
            class="ml-2 text-sm text-white p-2 x active:bg-gray-500 rounded"
          >
            <IconDelete />
          </button>
          <button
            class="ml-2 text-sm bg-gray-300 text-white p-2 x active:bg-gray-500 rounded"
            @click="
              () => {
                handleToggle(i);
              }
            "
          >
            <IconChevronLeft
              :class="sectionStatus[i] ? 'rotate-90' : '-rotate-90'"
            />
          </button>
        </div>
      </div>
      <BlogSection
        v-if="sectionStatus[i]"
        :formData="formDataLocal"
        :index="i"
        :validationStatus="validationStatusLocal"
        @update:validationStatus="handleUpdateValidationStatus"
      />
    </div>
    <!-- }}} -->
  </div>
</template>

<script>
import * as Actions from "@/store/actions.type";
import * as Constants from "@/store/constants";
import AttrTitleWithDescription from "@/components/Shared/Headers/AttrTitleWithDescription";
import BlogSection from "@/components/ComponentModule/BlogComponent/BlogSection.vue";
import IconDelete from "@/assets/icons/IconDelete";
import IconChevronLeft from "@/assets/icons/IconChevronLeft";
import { mapGetters } from "vuex";

export default {
  name: "BlogForm",
  components: {
    BlogSection,
    AttrTitleWithDescription,
    IconDelete,
    IconChevronLeft
  },
  data() {
    return {
      shortCodeCopied: false,
      sectionStatus: []
    };
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    validationStatus: {
      type: Boolean,
      required: true
    },
    pageStatus: {
      type: String,
      required: true
    }
  },
  computed: {
    validationStatusLocal: {
      get: function () {
        return this.validationStatus;
      },
      set: function (val) {
        this.$emit("update:validationStatus", val);
      }
    },
    blogTitle() {
      return this.formDataLocal["blog"]["abi_blog_title"];
    },
    basicBlogComponents() {
      return Constants.BLOG_COMPONENTS.basic;
    },
    formDataLocal: {
      get: function () {
        return this.formData;
      },
      set: function (val) {
        this.$emit("update:formData", val);
      }
    },
    ...mapGetters([
      "getAllComponent",
      "getBlogSelectedInstance",
      "blogOperationMode",
      "getBlogComponentAttrs",
      "getBlogComponentInstances",
      "getRegion"
    ])
  },
  mounted() {
    this.setComponentList();
    // if blog title is empty, not allow user to save the instance
    if (!this.formData.blog.abi_blog_title) {
      this.validationStatusLocal = false;
    }
    // if blog instance name is empty, not allow user to save the instance
    if (this.pageStatus === "create" && !this.formData.instanceName) {
      this.validationStatusLocal = false;
    }
    // fetch blog instances
    if (!this.getAllComponent.length) {
      this.$store.dispatch(Actions.FETCH_ALL_COMPONENT, {
        region: this.getRegion,
        hardRefresh: false
      });
    }
    this.$store.dispatch(Actions.FETCH_BLOG_COMPONENT_INSTANCE, {
      region: this.getRegion,
      componentId: this.getComponentId("abi_blog")
    });

    // set the section status
    this.formDataLocal["components"].forEach((item, i) => {
      this.sectionStatus[i] = false;
    });
  },
  unmounted() {
    // clear the page cache when unmount
    this.$store.dispatch(Actions.CLEAR_BLOG_COMPONENT_ATTR);
    this.$store.dispatch(Actions.CLEAR_BLOG_COMPONENT_LOADING);
    this.$store.dispatch(Actions.CLEAR_BLOG_SELECTED_INSTANCE);
  },
  watch: {
    "formData.components": {
      handler: function () {
        this.setComponentList();
      },
      deep: true
    },
    "formData.blog.abi_blog_title": {
      handler: function () {
        if (!this.formData.blog.abi_blog_title) {
          this.$store.dispatch(Actions.SET_BLOG_ERRORS, {
            error: "Please enter a Blog title! "
          });
          this.validationStatusLocal = false;
        } else {
          this.$store.dispatch(Actions.CLEAR_BLOG_ERRORS);
          this.validationStatusLocal = true;
        }
      },
      deep: true
    },
    "formData.instanceName": {
      handler: function () {
        if (!this.formData.instanceName) {
          this.$store.dispatch(Actions.SET_BLOG_ERRORS, {
            error: "Please enter a blog instance name! "
          });
          this.validationStatusLocal = false;
        } else {
          this.$store.dispatch(Actions.CLEAR_BLOG_ERRORS);
          this.validationStatusLocal = true;
          if (Object.keys(this.getBlogComponentInstances).length) {
            for (let instance of this.getBlogComponentInstances[
              this.getComponentId("abi_blog")
            ]) {
              if (
                instance.instance_name.split("|")[1] ===
                this.formData.instanceName
              ) {
                this.$store.dispatch(Actions.SET_BLOG_ERRORS, {
                  error: "Blog instance already exist! "
                });
                this.validationStatusLocal = false;
                break;
              } else {
                this.$store.dispatch(Actions.CLEAR_BLOG_ERRORS);
                this.validationStatusLocal = true;
              }
            }
          }
        }
      },
      deep: true
    }
  },
  methods: {
    setComponentList() {
      const components = this.formData["components"]
        .map((component, i) => {
          if (component.shouldCreate === "yes") {
            return this.blogSectionNameFormatter(
              component.component,
              component.name
            );
          } else {
            return this.getBlogSelectedInstance[i];
          }
        })
        .join(",");
      this.formDataLocal["blog"]["abi_blog_components"] = components;
    },
    blogSectionNameFormatter(component, sectionName) {
      return `${this.getComponentId(component) || ""}|blog_${
        sectionName || ""
      }`;
    },
    componentNameFormat(name) {
      return name.split("_").splice(1).join(" ").toUpperCase();
    },
    changeOptionsFormat(options) {
      const newOptions = options.map((item) => {
        return { label: item, value: item };
      });
      return newOptions;
    },
    handleAddSection() {
      this.formDataLocal["components"].push({
        attributes: {},
        component: "",
        componentId: "",
        name: "",
        id: "",
        shouldCreate: "no"
      });
    },
    getComponentId(componentName) {
      if (this.getAllComponent.find((item) => item.name === componentName)) {
        return this.getAllComponent.find((item) => item.name === componentName)
          .id;
      } else {
        return null;
      }
    },
    getComponentName(componentId) {
      if (this.getAllComponent.find((item) => item.id === componentId)) {
        return this.getAllComponent.find((item) => item.id === componentId)
          .name;
      } else {
        return null;
      }
    },
    handleToggle(index) {
      this.sectionStatus[index] = !this.sectionStatus[index];
    },
    handleDelete(index) {
      this.$store.dispatch(Actions.DELETE_SELECTED_INSTANCE, { index });
      this.$store.dispatch(Actions.DELETE_COMPONENT_ATTRIBUTE, { index });
      this.formDataLocal.components.splice(index, 1);
      this.formDataLocal.blog.abi_blog_components =
        this.formDataLocal.blog.abi_blog_components
          .split(",")
          .splice(index, 1)
          .join();
    },
    handleUpdateValidationStatus(val) {
      this.validationStatusLocal = val;
    },
    handleCopyShortcode() {
      const componentList = this.formData.blog.abi_blog_components.split(",");
      let shortcode = "";
      componentList.forEach((item) => {
        const id = item.split("|")[0];
        const componentName = this.getComponentName(id);
        const name = item.split("|")[1];
        shortcode += `[${componentName
          .toLowerCase()
          .replaceAll(" ", "_")} name=${id}|${name}]`;
      });
      navigator.clipboard.writeText(shortcode);
      this.shortCodeCopied = true;
      setTimeout(() => {
        this.shortCodeCopied = false;
      }, 3000);
    }
  }
};
</script>
